import { useMemo, useState } from "react";
import { type FieldValues, useFieldArray, useFormContext } from "react-hook-form";
import { commonAdapters } from "src/common-adapters";
import { startOfMonth } from "date-fns";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { DateScheduleConfig, DayTemplate, HalfDay, HalfDayLockPredicate, HalfDayScheduleConfig } from "./training-schedule-form.types";
import { applyDayTemplate, calculateTotalHours } from "./training-schedule-form.utils";

const NEVER_LOCKED: HalfDayLockPredicate = () => false;

interface UseTrainingScheduleParams {
  initialStartDate: Date;
  createSchedule: (date: Date) => DateScheduleConfig;
  /**
   * Locking policy of the form (legacy batches lock already scheduled half-days):
   * a locked half-day keeps its times and enabled state, and its date cannot be removed.
   */
  isHalfDayLocked?: HalfDayLockPredicate;
}

interface ScheduleFormInputs extends FieldValues {
  trainingSchedules: DateScheduleConfig[];
}

export const useTrainingSchedule = ({ initialStartDate, createSchedule, isHalfDayLocked = NEVER_LOCKED }: UseTrainingScheduleParams) => {
  const { dateProvider } = commonAdapters;
  const { control, clearErrors } = useFormContext<ScheduleFormInputs>();
  const [currentMonth] = useState<Nullable<Date>>(startOfMonth(initialStartDate));

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: "trainingSchedules",
  });

  const totalHours = useMemo(() => calculateTotalHours(fields), [fields]);
  const selectedDates = useMemo(() => fields.map(s => s.date), [fields]);

  const sortedFieldsWithIndex = useMemo(() => {
    return fields.map((field, index) => ({ field, originalIndex: index })).sort((a, b) => a.field.date.getTime() - b.field.date.getTime());
  }, [fields]);

  const bankHolidaysSelected = useMemo(() => {
    return fields.filter(({ date }) => dateProvider!.isBankHoliday(date));
  }, [fields, dateProvider]);

  const isDateLocked = (date: Date): boolean => isHalfDayLocked(date, "morning") || isHalfDayLocked(date, "afternoon");

  const handleRemove = (index: number) => {
    const schedule = fields[index];
    if (!schedule || isDateLocked(schedule.date)) return;

    clearErrors("trainingSchedules");
    remove(index);
  };

  const onCalendarSelect = (dates: Date[]) => {
    const hasAddedANewDate = dates.length > fields.length;
    if (hasAddedANewDate) {
      const lastSelectedDate = dates[dates.length - 1];
      if (lastSelectedDate) {
        const isNewDate = !fields.find(s => s.date.getTime() === lastSelectedDate.getTime());
        if (isNewDate) {
          append(createSchedule(lastSelectedDate));
        }
      }
    } else {
      const removedIndex = fields.findIndex(s => !dates.find(d => d.getTime() === s.date.getTime()));
      if (removedIndex !== -1) {
        handleRemove(removedIndex);
      }
    }
  };

  const handleHalfDayChange = (index: number, halfDay: HalfDay, changes: Partial<HalfDayScheduleConfig>) => {
    const schedule = fields[index];
    if (!schedule || isHalfDayLocked(schedule.date, halfDay)) return;

    update(index, { ...schedule, [halfDay]: { ...schedule[halfDay], ...changes } });
  };

  const applyDayTemplateToAll = (template: DayTemplate) => {
    clearErrors("trainingSchedules");
    replace(fields.map(schedule => applyDayTemplate(schedule, template, isHalfDayLocked)));
  };

  const handleClearAll = () => {
    clearErrors("trainingSchedules");
    replace(fields.filter(({ date }) => isDateLocked(date)));
  };

  return {
    control,
    fields,
    update,
    replace,
    currentMonth,
    totalHours,
    selectedDates,
    sortedFieldsWithIndex,
    bankHolidaysSelected,
    isDateLocked,
    onCalendarSelect,
    handleHalfDayChange,
    applyDayTemplateToAll,
    handleRemove,
    handleClearAll,
  };
};
